import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input, InputProvider } from "@redotech/ui/form";
import classNames from "classnames";
import { memo, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";

export type ImsStatus = "read-only" | "pending" | "source-of-truth";

function formatElapsedTime(startTime: Date): string {
  const now = new Date();
  const elapsedMs = now.getTime() - new Date(startTime).getTime();
  const totalSeconds = Math.floor(elapsedMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

export const imsGeneralForm = groupInput({
  enabled: input<boolean>(),
  syncEnabled: input<boolean>(),
  catalogEnabled: input<boolean>(),
  catalogSourceOfTruthStatus: input<ImsStatus>(),
  forecastingEnabled: input<boolean>(),
  pricingRulesEnabled: input<boolean>(),
  status: input<ImsStatus>(),
  imsBillingEnabled: input<boolean>(),
  imsBillingRenewOnPeriodEnd: input<boolean>(),
  imsBillingPriceInCents: input<number>(),
  imsBillingPeriod: input<string>(),
  forecastingBillingEnabled: input<boolean>(),
  forecastingBillingRenewOnPeriodEnd: input<boolean>(),
  forecastingUseSubscriptionPricing: input<boolean>(),
  forecastingCustomPriceInCents: input<number>(),
  forecastingBillingPeriod: input<string>(),
  orderSyncTemporal: input<boolean>(),
});

export type ImsGeneralForm = InputProvider.Form<typeof imsGeneralForm>;

export type ImsGeneralValue = InputProvider.Value<typeof imsGeneralForm>;

export const imsGeneralDefault: ImsGeneralValue = {
  enabled: false,
  syncEnabled: false,
  catalogEnabled: false,
  catalogSourceOfTruthStatus: "read-only" as ImsStatus,
  forecastingEnabled: false,
  pricingRulesEnabled: false,
  status: "read-only" as ImsStatus,
  imsBillingEnabled: false,
  imsBillingRenewOnPeriodEnd: true,
  imsBillingPriceInCents: 50_000,
  imsBillingPeriod: "monthly",
  forecastingBillingEnabled: false,
  forecastingBillingRenewOnPeriodEnd: true,
  forecastingUseSubscriptionPricing: false,
  forecastingCustomPriceInCents: 50_000,
  forecastingBillingPeriod: "monthly",
  orderSyncTemporal: false,
};

export const ImsGeneralCard = memo(function ImsGeneralCard({
  input,
  initialStatus,
  onSourceOfTruthToggleChange,
  onOpenForecastingBreakdown,
  imsBillingStartDate,
  forecastingBillingStartDate,
  selfEnroll,
}: {
  input: ImsGeneralForm;
  initialStatus: ImsStatus;
  onSourceOfTruthToggleChange: (value: boolean) => void;
  onOpenForecastingBreakdown?: () => void;
  /** If set, IMS billing has started and the enabled toggle should be disabled */
  imsBillingStartDate?: Date | null;
  /** If set, Forecasting billing has started and the enabled toggle should be disabled */
  forecastingBillingStartDate?: Date | null;
  /** Whether the customer self-enrolled */
  selfEnroll?: boolean;
}) {
  const params = useParams();
  const teamId = params.teamId!;
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);

  const {
    enabled,
    syncEnabled,
    forecastingEnabled,
    pricingRulesEnabled,
    status,
    imsBillingEnabled,
    imsBillingRenewOnPeriodEnd,
    imsBillingPriceInCents,
    imsBillingPeriod,
    forecastingBillingEnabled,
    forecastingBillingRenewOnPeriodEnd,
    forecastingCustomPriceInCents,
    forecastingBillingPeriod,
  } = input.inputs;

  const [forecastStatusVersion, setForecastStatusVersion] = useState(0);
  const [triggerError, setTriggerError] = useState<string | null>(null);
  const [isTriggering, setIsTriggering] = useState(false);
  const [elapsedTime, setElapsedTime] = useState<string | null>(null);

  const forecastStatus = useLoad(
    () => rpcClient.getForecastStatus({ teamId }),
    [rpcClient, teamId, forecastStatusVersion],
  );

  // Update elapsed time every second when a forecast is running
  useEffect(() => {
    if (
      !forecastStatus.pending &&
      !forecastStatus.error &&
      forecastStatus.value?.isForecasting &&
      forecastStatus.value?.workflowStartTime
    ) {
      const updateElapsedTime = () => {
        setElapsedTime(
          formatElapsedTime(forecastStatus.value!.workflowStartTime!),
        );
      };
      updateElapsedTime();
      const interval = setInterval(updateElapsedTime, 1000);
      return () => clearInterval(interval);
    }
    setElapsedTime(null);
    return undefined;
  }, [forecastStatus.pending, forecastStatus.error, forecastStatus.value]);

  // Poll for status updates while a forecast is running
  useEffect(() => {
    if (
      !forecastStatus.pending &&
      !forecastStatus.error &&
      forecastStatus.value?.isForecasting
    ) {
      const pollInterval = setInterval(() => {
        setForecastStatusVersion((v) => v + 1);
      }, 30_000); // Poll every 30 seconds
      return () => clearInterval(pollInterval);
    }
    return undefined;
  }, [
    forecastStatus.pending,
    forecastStatus.error,
    forecastStatus.value?.isForecasting,
  ]);

  const handleTriggerForecast = async () => {
    setIsTriggering(true);
    setTriggerError(null);
    try {
      const result = await rpcClient.triggerForecast({ teamId });
      if (!result.success && result.error) {
        setTriggerError(result.error);
      } else {
        // Refresh status to show the running workflow
        setForecastStatusVersion((v) => v + 1);
      }
    } catch (error) {
      setTriggerError(error instanceof Error ? error.message : "Unknown error");
    } finally {
      setIsTriggering(false);
    }
  };

  const getStatusLabel = (status: ImsStatus) => {
    switch (status) {
      case "read-only":
        return "Read Only (Default)";
      case "pending":
        return "Pending Activation";
      case "source-of-truth":
        return "Full Source of Truth";
      default:
        return "Unknown";
    }
  };

  const handleImsEnabledChange = (value: boolean) => {
    if (value && !syncEnabled.value) {
      // If trying to enable IMS, sync must be enabled first
      return;
    }
    enabled.setValue(value);
  };

  const handleSyncEnabledChange = (value: boolean) => {
    if (!value) {
      // If disabling sync, also disable IMS, forecasting, and billing
      enabled.setValue(false);
      forecastingEnabled.setValue(false);
      pricingRulesEnabled.setValue(false);
      imsBillingEnabled.setValue(false);
      forecastingBillingEnabled.setValue(false);
    }
    syncEnabled.setValue(value);
  };

  return (
    <Card title="Inventory Management System">
      <section className={gridCss.grid}>
        <div className={gridCss.span12}>
          <div className={gridCss.grid}>
            <div className={classNames(gridCss.span6L, gridCss.span12)}>
              <RedoToggle
                description="Enable product and inventory sync from Shopify"
                label="Sync Enabled"
                setValue={handleSyncEnabledChange}
                size="sm"
                value={syncEnabled.value ?? false}
              />
            </div>
            <div className={classNames(gridCss.span6L, gridCss.span12)}>
              <RedoToggle
                description={
                  !syncEnabled.value
                    ? "Enable sync first to use full IMS functionality"
                    : "Enable full Inventory Management System functionality"
                }
                disabled={!syncEnabled.value}
                label="IMS Enabled"
                setValue={handleImsEnabledChange}
                size="sm"
                value={enabled.value ?? false}
              />
            </div>
            <div className={classNames(gridCss.span6L, gridCss.span12)}>
              <RedoToggle
                description={
                  forecastingEnabled.value &&
                  !forecastStatus.pending &&
                  !forecastStatus.error &&
                  forecastStatus.value?.lastForecastAt
                    ? `Enable sales forecasting for products (Last forecast: ${new Date(
                        forecastStatus.value.lastForecastAt,
                      ).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })})`
                    : "Enable sales forecasting for products"
                }
                disabled={!enabled.value}
                label="Forecasting Enabled"
                setValue={(value) => forecastingEnabled.setValue(value)}
                size="sm"
                value={forecastingEnabled.value ?? false}
              />
            </div>
            {forecastingEnabled.value && (
              <div
                className={classNames(gridCss.span6L, gridCss.span12)}
                style={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                  padding: "16px",
                }}
              >
                <Flex dir="column" gap="md">
                  <Flex align="center" gap="md">
                    <RedoButton
                      disabled={
                        isTriggering ||
                        (forecastStatus.value?.isForecasting ?? false) ||
                        forecastStatus.value?.hasSnowflakeData === false
                      }
                      onClick={handleTriggerForecast}
                      pending={isTriggering}
                      size="sm"
                      text={
                        forecastStatus.value?.isForecasting
                          ? "Forecast Running..."
                          : "Run Forecast"
                      }
                      theme="normal"
                    />
                    {forecastStatus.value?.hasSnowflakeData === false && (
                      <Text fontSize="sm" textColor="tertiary">
                        Data syncing into Snowflake. Should be ready in ~1 hour
                      </Text>
                    )}
                    {forecastStatus.value?.isForecasting && elapsedTime && (
                      <Text fontSize="sm" textColor="tertiary">
                        Running for {elapsedTime}
                      </Text>
                    )}
                  </Flex>
                  {triggerError && (
                    <Text fontSize="xs" textColor="error">
                      {triggerError}
                    </Text>
                  )}
                </Flex>
              </div>
            )}
            <div className={classNames(gridCss.span6L, gridCss.span12)}>
              <RedoToggle
                description="Enable pricing rules for inventory management"
                disabled={!enabled.value}
                label="Pricing Rules Enabled"
                setValue={(value) => pricingRulesEnabled.setValue(value)}
                size="sm"
                value={pricingRulesEnabled.value ?? false}
              />
            </div>
            <div className={classNames(gridCss.span6L, gridCss.span12)}>
              <RedoToggle
                description={getStatusLabel(status.value || "read-only")}
                disabled={!enabled.value || initialStatus !== "read-only"}
                label="Source of Truth"
                setValue={onSourceOfTruthToggleChange}
                size="sm"
                value={
                  status.value === "source-of-truth" ||
                  status.value === "pending"
                }
              />
            </div>
            {/* Billing Section */}
            <div className={gridCss.span12}>
              <Flex align="baseline" gap="sm">
                <Text
                  fontSize="md"
                  fontWeight="semibold"
                  style={{ marginTop: "24px", marginBottom: "16px" }}
                >
                  Billing
                </Text>
                {selfEnroll && (
                  <Text
                    fontSize="xs"
                    style={{ marginTop: "24px", marginBottom: "16px" }}
                    textColor="tertiary"
                  >
                    (self-enrolled)
                  </Text>
                )}
              </Flex>
            </div>
            <div
              className={classNames(gridCss.span6L, gridCss.span12)}
              style={{
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
                padding: "16px",
              }}
            >
              <Flex dir="column" gap="md">
                <RedoToggle
                  description={
                    imsBillingStartDate
                      ? "Billing has started. To cancel, disable auto-renew instead."
                      : "Enable IMS subscription billing"
                  }
                  disabled={!!imsBillingStartDate}
                  label="IMS Subscription"
                  setValue={(value) => imsBillingEnabled.setValue(value)}
                  size="sm"
                  value={imsBillingEnabled.value ?? false}
                />
                {imsBillingEnabled.value && (
                  <>
                    <RedoToggle
                      description="Automatically renew subscription at the end of each billing period"
                      label="Auto-renew"
                      setValue={(value) =>
                        imsBillingRenewOnPeriodEnd.setValue(value)
                      }
                      size="sm"
                      value={imsBillingRenewOnPeriodEnd.value ?? true}
                    />
                    <Flex align="center" gap="md">
                      <Flex align="baseline" gap="xs">
                        <Text fontSize="sm" fontWeight="semibold">
                          Price
                        </Text>
                        <Text fontSize="xs" textColor="tertiary">
                          (
                          {imsBillingPeriod.value === "yearly"
                            ? "annual"
                            : "monthly"}
                          )
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold">
                          :
                        </Text>
                      </Flex>
                      <Flex align="center" gap="xs">
                        <Text fontSize="sm">$</Text>
                        <RedoTextInput
                          setValue={(value: string) => {
                            const cents = Math.round(Number(value) * 100);
                            imsBillingPriceInCents.setValue(
                              isNaN(cents) ? 0 : cents,
                            );
                          }}
                          size="sm"
                          value={(
                            (imsBillingPriceInCents.value ?? 0) / 100
                          ).toFixed(2)}
                        />
                      </Flex>
                    </Flex>
                  </>
                )}
              </Flex>
            </div>
            <div
              className={classNames(gridCss.span6L, gridCss.span12)}
              style={{
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
                padding: "16px",
              }}
            >
              <Flex dir="column" gap="md">
                <RedoToggle
                  description={
                    forecastingBillingStartDate
                      ? "Billing has started. To cancel, disable auto-renew instead."
                      : "Enable forecasting billing"
                  }
                  disabled={!!forecastingBillingStartDate}
                  label="Forecasting Billing"
                  setValue={(value) =>
                    forecastingBillingEnabled.setValue(value)
                  }
                  size="sm"
                  value={forecastingBillingEnabled.value ?? false}
                />
                {forecastingBillingEnabled.value && (
                  <>
                    <RedoToggle
                      description="Automatically renew subscription at the end of each billing period"
                      label="Auto-renew"
                      setValue={(value) =>
                        forecastingBillingRenewOnPeriodEnd.setValue(value)
                      }
                      size="sm"
                      value={forecastingBillingRenewOnPeriodEnd.value ?? true}
                    />
                    <Flex align="center" gap="md">
                      <Flex align="baseline" gap="xs">
                        <Text fontSize="sm" fontWeight="semibold">
                          Price
                        </Text>
                        <Text fontSize="xs" textColor="tertiary">
                          (
                          {forecastingBillingPeriod.value === "yearly"
                            ? "annual"
                            : "monthly"}
                          )
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold">
                          :
                        </Text>
                      </Flex>
                      <Flex align="center" gap="xs">
                        <Text fontSize="sm">$</Text>
                        <RedoTextInput
                          setValue={(value: string) => {
                            const cents = Math.round(Number(value) * 100);
                            forecastingCustomPriceInCents.setValue(
                              isNaN(cents) ? 0 : cents,
                            );
                          }}
                          size="sm"
                          value={(
                            (forecastingCustomPriceInCents.value ?? 0) / 100
                          ).toFixed(2)}
                        />
                      </Flex>
                    </Flex>
                    {onOpenForecastingBreakdown && (
                      <RedoButton
                        hierarchy="secondary"
                        onClick={onOpenForecastingBreakdown}
                        size="xs"
                        text="View breakdown"
                        theme="normal"
                      />
                    )}
                  </>
                )}
              </Flex>
            </div>
          </div>
        </div>
      </section>
    </Card>
  );
});
